<script setup>
import { ref, computed, onMounted, reactive, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { debounce } from '@chatwoot/utils';
import { useCompaniesStore } from 'dashboard/stores/companies';
import { useStore, useStoreGetters } from 'dashboard/composables/store';

import CompaniesListLayout from 'dashboard/components-next/Companies/CompaniesListLayout.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import CompanyImportDialog from 'dashboard/components-next/Companies/CompaniesForm/CompanyImportDialog.vue';
import BulkActionsAPI from 'dashboard/api/bulkActions';

const DEFAULT_SORT_FIELD = 'name';
const DEBOUNCE_DELAY = 300;

const companiesStore = useCompaniesStore();
const store = useStore();
const getters = useStoreGetters();

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const { updateUISettings, uiSettings } = useUISettings();

const companies = computed(() => companiesStore.getCompaniesList);
const meta = computed(() => companiesStore.getMeta);
const uiFlags = computed(() => companiesStore.getUIFlags);
const fieldDefs = computed(() => getters['companyFieldDefinitions/getAll'].value);

const searchQuery = computed(() => route.query?.search || '');
const searchValue = ref(searchQuery.value);
const pageNumber = computed(() => Number(route.query?.page) || 1);

// ── Sort ──────────────────────────────────────────────────────────────────────
const parseSortSettings = (sortString = '') => {
  const hasDescending = sortString.startsWith('-');
  const sortField = hasDescending ? sortString.slice(1) : sortString;
  return { sort: sortField || DEFAULT_SORT_FIELD, order: hasDescending ? '-' : '' };
};

const { companies_sort_by: companySortBy = DEFAULT_SORT_FIELD } = uiSettings.value ?? {};
const { sort: initialSort, order: initialOrder } = parseSortSettings(companySortBy);

const sortState = reactive({ activeSort: initialSort, activeOrdering: initialOrder });
const activeSort = computed(() => sortState.activeSort);
const activeOrdering = computed(() => sortState.activeOrdering);
const isFetchingList = computed(() => uiFlags.value.fetchingList);
const buildSortAttr = () => `${sortState.activeOrdering}${sortState.activeSort}`;
const sortParam = computed(() => buildSortAttr());

// ── Fetch ─────────────────────────────────────────────────────────────────────
const updateURLParams = (page, search = '', sort = '') => {
  const query = { ...route.query, page: page.toString() };
  if (search) query.search = search; else delete query.search;
  if (sort)   query.sort   = sort;   else delete query.sort;
  router.replace({ query });
};

const fetchCompanies = async (page, search, sort) => {
  const currentPage   = page   ?? pageNumber.value;
  const currentSearch = search ?? searchQuery.value;
  const currentSort   = sort   ?? sortParam.value;

  if (page !== undefined || search !== undefined || sort !== undefined) {
    updateURLParams(currentPage, currentSearch, currentSort);
  }

  const currentFilters = activeFilters.value.filter(f => f.field && (f.value || ['is_present', 'is_not_present'].includes(f.op)));
  if (currentSearch) {
    await companiesStore.search({ search: currentSearch, page: currentPage, sort: currentSort });
  } else {
    await companiesStore.get({ page: currentPage, sort: currentSort, filters: currentFilters });
  }
};

const onSearch   = debounce(q => { searchValue.value = q; fetchCompanies(1, q, sortParam.value); }, DEBOUNCE_DELAY);
const onPageChange = page => fetchCompanies(page, searchValue.value, sortParam.value);

const handleSort = async ({ sort, order }) => {
  Object.assign(sortState, { activeSort: sort, activeOrdering: order });
  await updateUISettings({ companies_sort_by: buildSortAttr() });
  fetchCompanies(1, searchValue.value, buildSortAttr());
};

// ── Dialog ────────────────────────────────────────────────────────────────────
const dialogRef   = ref(null);
const editingId   = ref(null);
const deletingId  = ref(null);

const FIELD_TYPES = [
  { id: 'text',     label: 'Text',     icon: 'i-lucide-type' },
  { id: 'number',   label: 'Number',   icon: 'i-lucide-hash' },
  { id: 'date',     label: 'Date',     icon: 'i-lucide-calendar' },
  { id: 'link',     label: 'Link',     icon: 'i-lucide-link' },
  { id: 'list',     label: 'List',     icon: 'i-lucide-list' },
  { id: 'checkbox', label: 'Checkbox', icon: 'i-lucide-check-square' },
];

const form = ref({ name: '', domain: '', description: '', extraFields: {} });

const openAdd = () => {
  editingId.value = null;
  form.value = { name: '', domain: '', description: '', extraFields: {} };
  dialogRef.value?.open();
};

const openEdit = id => {
  const company = companies.value.find(c => c.id === id);
  if (!company) return;
  editingId.value = id;
  const extra = {};
  fieldDefs.value.forEach(fd => { extra[fd.field_key] = company.extraFields?.[snakeToCamel(fd.field_key)] ?? ''; });
  form.value = {
    name: company.name || '',
    domain: company.domain || '',
    description: company.description || '',
    extraFields: extra,
  };
  dialogRef.value?.open();
};

const closeDialog = () => { dialogRef.value?.close(); editingId.value = null; };

const saveCompany = async () => {
  const companyDomain = (form.value.extraFields.company_domain || '').trim();
  if (!companyDomain) return;
  const payload = {
    name: companyDomain,
    domain: companyDomain,
    description: form.value.description.trim(),
    extra_fields: form.value.extraFields,
  };
  try {
    if (editingId.value) {
      await companiesStore.updateCompany({ id: editingId.value, ...payload });
      useAlert('Company updated');
    } else {
      await companiesStore.createCompany(payload);
      useAlert('Company created');
    }
    closeDialog();
  } catch {
    useAlert('Failed to save company');
  }
};

// ── Delete ────────────────────────────────────────────────────────────────────
const deleteCompany = async id => {
  const company = companies.value.find(c => c.id === id);
  if (!confirm(`Delete "${company?.name}"? This cannot be undone.`)) return;
  deletingId.value = id;
  try {
    await companiesStore.deleteCompany(id);
    useAlert('Company deleted');
  } catch {
    useAlert('Failed to delete company');
  } finally {
    deletingId.value = null;
  }
};

// ── Import ────────────────────────────────────────────────────────────────────
const importDialogRef = ref(null);

const openImport = () => importDialogRef.value?.open();

const onImportFile = async file => {
  try {
    await companiesStore.importCompanies(file);
    useAlert('Import started — you will receive an email when complete');
  } catch {
    useAlert('Import failed');
  }
};

// ── Export ────────────────────────────────────────────────────────────────────
const exporting = ref(false);

const exportCompanies = async () => {
  exporting.value = true;
  try {
    await companiesStore.exportCompanies();
    useAlert('Export started — you will receive an email with the download link');
  } catch {
    useAlert('Export failed');
  } finally {
    exporting.value = false;
  }
};

const goToCompany = id => {
  router.push(`/app/accounts/${route.params.accountId}/companies/${id}`);
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const snakeToCamel = s => s.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
const getExtraField = (company, fieldKey) => company.extraFields?.[snakeToCamel(fieldKey)] ?? '';

const formatNumber = (value, fmt = 'comma_dot') => {
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  const [intRaw, decRaw] = num.toString().split('.');
  const dec = decRaw ? decRaw.substring(0, 4) : null;
  let intFormatted;
  if (fmt === 'indian') {
    if (intRaw.length <= 3) { intFormatted = intRaw; }
    else {
      const last3 = intRaw.slice(-3);
      const rest = intRaw.slice(0, -3);
      intFormatted = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + last3;
    }
  } else if (fmt === 'dot_comma') {
    intFormatted = intRaw.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  } else if (fmt === 'space_comma') {
    intFormatted = intRaw.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  } else {
    intFormatted = intRaw.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  const sep = (fmt === 'dot_comma' || fmt === 'space_comma') ? ',' : '.';
  return dec ? `${intFormatted}${sep}${dec}` : intFormatted;
};

const displayFieldValue = (company, fd) => {
  const raw = getExtraField(company, fd.field_key);
  if (raw === '' || raw == null) return '—';
  if (fd.field_type === 'number') return formatNumber(raw, fd.field_config?.format);
  if (fd.field_type === 'checkbox') return raw ? 'Yes' : 'No';
  return raw;
};

// ── Inline editing ────────────────────────────────────────────────────────────
const editingCell = ref(null); // { companyId, fieldKey }
const editingValue = ref('');

const startInlineEdit = (e, company, fd) => {
  if (fd.read_only || fd.field_type === 'auto_number') return;
  if (fd.field_type === 'checkbox') {
    const raw = companiesStore.records.find(r => r.id === company.id);
    const extra = { ...(raw?.extra_fields || {}) };
    extra[fd.field_key] = !getExtraField(company, fd.field_key);
    companiesStore.updateCompany({ id: company.id, extra_fields: extra }).catch(() => useAlert('Failed to save'));
    return;
  }
  editingCell.value = { companyId: company.id, fieldKey: fd.field_key };
  editingValue.value = String(getExtraField(company, fd.field_key) || '');
};

const saveInlineEdit = async () => {
  if (!editingCell.value) return;
  const { companyId, fieldKey } = editingCell.value;
  editingCell.value = null;
  const raw = companiesStore.records.find(r => r.id === companyId);
  const extra = { ...(raw?.extra_fields || {}) };
  extra[fieldKey] = editingValue.value;
  try {
    await companiesStore.updateCompany({ id: companyId, extra_fields: extra });
  } catch {
    useAlert('Failed to save');
  }
};

const cancelInlineEdit = () => { editingCell.value = null; };
const formatDate = ts => {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

// ── Bulk selection ────────────────────────────────────────────────────────────
const selectedCompanyIds = ref([]);
const isBulkLoading = ref(false);
const selectedSet = computed(() => new Set(selectedCompanyIds.value));
const hasSelection = computed(() => selectedCompanyIds.value.length > 0);

const isCompanySelected = id => selectedSet.value.has(id);
const toggleCompanySelect = (id, value) => {
  if (value) {
    if (!selectedSet.value.has(id)) selectedCompanyIds.value = [...selectedCompanyIds.value, id];
  } else {
    selectedCompanyIds.value = selectedCompanyIds.value.filter(cid => cid !== id);
  }
};
const toggleSelectAll = shouldSelect => {
  if (shouldSelect) {
    selectedCompanyIds.value = companies.value.map(c => c.id);
  } else {
    selectedCompanyIds.value = [];
  }
};
const clearSelection = () => { selectedCompanyIds.value = []; };

// Bulk field update panel
const showBulkPanel = ref(false);
const COMPANY_SYSTEM_FIELDS = [
  { key: 'name', label: 'Name', fieldType: 'text', isCustom: false },
  { key: 'domain', label: 'Domain', fieldType: 'text', isCustom: false },
  { key: 'description', label: 'Description', fieldType: 'text', isCustom: false },
];
const bulkFields = computed(() => [
  ...COMPANY_SYSTEM_FIELDS,
  ...fieldDefs.value.map(fd => ({
    key: fd.field_key,
    label: fd.label,
    fieldType: fd.field_type,
    options: fd.options || [],
    isCustom: true,
  })),
]);
const bulkUpdate = reactive({ fieldKey: '', value: '', fieldType: 'text', isCustom: false, options: [] });

const selectBulkField = key => {
  const f = bulkFields.value.find(x => x.key === key);
  if (!f) return;
  Object.assign(bulkUpdate, { fieldKey: f.key, fieldType: f.field_type || f.fieldType, isCustom: f.isCustom, options: f.options || [], value: '' });
};

const openBulkPanel = () => {
  showBulkPanel.value = true;
  const first = bulkFields.value[0];
  if (first) selectBulkField(first.key);
};
const closeBulkPanel = () => { showBulkPanel.value = false; };

const applyBulkUpdate = async () => {
  if (!bulkUpdate.fieldKey || !selectedCompanyIds.value.length) return;
  isBulkLoading.value = true;
  try {
    await BulkActionsAPI.create({
      type: 'Company',
      ids: selectedCompanyIds.value,
      action_name: 'update_field',
      action_attributes: { field_key: bulkUpdate.fieldKey, value: bulkUpdate.value, is_custom: bulkUpdate.isCustom },
    });
    useAlert('Field updated for selected companies');
    clearSelection();
    closeBulkPanel();
    await fetchCompanies();
  } catch {
    useAlert('Failed to update companies');
  } finally {
    isBulkLoading.value = false;
  }
};

// ── Filter ────────────────────────────────────────────────────────────────────
const showFilterPanel = ref(false);
const filterPanelStyle = ref({});
const filterButtonRef = ref(null);
const activeFilters = ref([]);

const FILTER_OPERATORS = [
  { value: 'contains',       label: 'Contains' },
  { value: 'not_contains',   label: 'Does not contain' },
  { value: 'equals',         label: 'Equals' },
  { value: 'starts_with',    label: 'Starts with' },
  { value: 'is_present',     label: 'Is present' },
  { value: 'is_not_present', label: 'Is not present' },
];

const filterFields = computed(() => {
  const system = [
    { value: 'company_domain', label: 'Company Domain' },
    { value: 'description',    label: 'Description' },
  ];
  const custom = (fieldDefs.value || []).map(fd => ({ value: fd.field_key, label: fd.label }));
  return [...system, ...custom];
});

const hasActiveFilters = computed(() => activeFilters.value.length > 0);

const addFilterRow = () => {
  activeFilters.value = [
    ...activeFilters.value,
    { field: filterFields.value[0]?.value || 'company_domain', op: 'contains', value: '' },
  ];
};

const removeFilterRow = idx => {
  activeFilters.value = activeFilters.value.filter((_, i) => i !== idx);
};

const updateFilter = (idx, key, val) => {
  const updated = activeFilters.value.map((f, i) => (i === idx ? { ...f, [key]: val } : f));
  activeFilters.value = updated;
};

const clearFilters = () => {
  activeFilters.value = [];
  fetchCompanies(1, searchValue.value, sortParam.value);
  showFilterPanel.value = false;
};

const applyFilters = () => {
  const valid = activeFilters.value.filter(f => f.field && (f.value || ['is_present', 'is_not_present'].includes(f.op)));
  activeFilters.value = valid;
  fetchCompanies(1, searchValue.value, sortParam.value);
  showFilterPanel.value = false;
};

const openFilterPanel = () => {
  if (activeFilters.value.length === 0) addFilterRow();
  showFilterPanel.value = !showFilterPanel.value;
  if (showFilterPanel.value) {
    nextTick(() => {
      const btn = document.querySelector('[data-filter-btn]');
      if (btn) {
        const rect = btn.getBoundingClientRect();
        filterPanelStyle.value = {
          top: `${rect.bottom + 6}px`,
          left: `${rect.left}px`,
          width: '480px',
          zIndex: 9999,
        };
      }
    });
  }
};

// ── Column resize ─────────────────────────────────────────────────────────────
const COL_WIDTHS_KEY = 'companies_col_widths';
const colWidths = ref({});

const loadColWidths = () => {
  try {
    const saved = localStorage.getItem(COL_WIDTHS_KEY);
    if (saved) colWidths.value = JSON.parse(saved);
  } catch {}
};

const saveColWidths = () => {
  try {
    localStorage.setItem(COL_WIDTHS_KEY, JSON.stringify(colWidths.value));
  } catch {}
};

let resizeState = null;

const startResize = (e, key) => {
  e.preventDefault();
  e.stopPropagation();
  const th = e.currentTarget.parentElement;
  const startWidth = colWidths.value[key] || th.offsetWidth;
  resizeState = { key, startX: e.clientX, startWidth };

  const onMove = ev => {
    if (!resizeState) return;
    const newWidth = Math.max(60, resizeState.startWidth + (ev.clientX - resizeState.startX));
    colWidths.value = { ...colWidths.value, [resizeState.key]: newWidth };
  };

  const onUp = () => {
    saveColWidths();
    resizeState = null;
    document.removeEventListener('mousemove', onMove);
    document.removeEventListener('mouseup', onUp);
  };

  document.addEventListener('mousemove', onMove);
  document.addEventListener('mouseup', onUp);
};

const colStyle = key => {
  const w = colWidths.value[key];
  return w ? { width: `${w}px`, minWidth: `${w}px` } : {};
};

onMounted(() => {
  loadColWidths();
  searchValue.value = searchQuery.value;
  fetchCompanies();
  store.dispatch('companyFieldDefinitions/fetchAll');
});
</script>

<template>
  <CompaniesListLayout
    :search-value="searchValue"
    :header-title="t('COMPANIES.HEADER')"
    :current-page="pageNumber"
    :total-items="Number(meta.totalCount || 0)"
    :active-sort="activeSort"
    :active-ordering="activeOrdering"
    :is-fetching-list="isFetchingList"
    :show-pagination-footer="!!companies.length"
    :has-active-filters="hasActiveFilters"
    @update:current-page="onPageChange"
    @update:sort="handleSort"
    @search="onSearch"
    @add="openAdd"
    @import="openImport"
    @export="exportCompanies"
    @filter="openFilterPanel"
  >
    <div v-if="isFetchingList" class="flex items-center justify-center p-8">
      <span class="text-n-slate-11 text-base">{{ t('COMPANIES.LOADING') }}</span>
    </div>
    <div v-else-if="companies.length === 0" class="flex items-center justify-center p-8">
      <span class="text-n-slate-11 text-base">{{ t('COMPANIES.EMPTY_STATE.TITLE') }}</span>
    </div>
    <div v-else class="flex flex-col">
      <!-- Bulk action bar -->
      <div
        v-if="hasSelection"
        class="flex items-center gap-3 px-4 py-2 bg-n-alpha-2 border-b border-n-weak text-sm"
      >
        <span class="text-n-slate-11 font-medium">{{ selectedCompanyIds.length }} selected</span>
        <div class="relative">
          <button
            class="flex items-center gap-1.5 px-3 py-1 text-xs font-medium bg-white dark:bg-n-solid-2 border border-n-weak rounded hover:bg-n-alpha-1 text-n-slate-12 transition-colors"
            @click="showBulkPanel ? closeBulkPanel() : openBulkPanel()"
          >
            <span class="i-lucide-pencil-line size-3.5" />
            Update field
          </button>
          <!-- Bulk update panel -->
          <div
            v-if="showBulkPanel"
            class="absolute z-30 top-full left-0 mt-1 w-64 bg-n-solid-2 border border-n-weak rounded-lg shadow-lg p-3 flex flex-col gap-3"
          >
          <div>
            <label class="block text-xs font-medium text-n-slate-9 mb-1">Field</label>
            <select
              :value="bulkUpdate.fieldKey"
              class="w-full px-2 py-1.5 text-sm border border-n-weak rounded bg-white dark:bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500"
              @change="e => selectBulkField(e.target.value)"
            >
              <option v-for="f in bulkFields" :key="f.key" :value="f.key">{{ f.label }}</option>
            </select>
          </div>
          <div>
            <label class="block text-xs font-medium text-n-slate-9 mb-1">New value</label>
            <select
              v-if="bulkUpdate.fieldType === 'list'"
              v-model="bulkUpdate.value"
              class="w-full px-2 py-1.5 text-sm border border-n-weak rounded bg-white dark:bg-n-solid-1 text-n-slate-12 focus:outline-none"
            >
              <option value="">— clear —</option>
              <option v-for="opt in bulkUpdate.options" :key="opt" :value="opt">{{ opt }}</option>
            </select>
            <div v-else-if="bulkUpdate.fieldType === 'checkbox'" class="flex items-center gap-2">
              <input v-model="bulkUpdate.value" type="checkbox" class="w-4 h-4 accent-woot-500" />
              <span class="text-sm text-n-slate-11">{{ bulkUpdate.value ? 'Yes' : 'No' }}</span>
            </div>
            <input
              v-else
              v-model="bulkUpdate.value"
              :type="bulkUpdate.fieldType === 'number' ? 'number' : 'text'"
              placeholder="Enter value"
              class="w-full px-2 py-1.5 text-sm border border-n-weak rounded bg-white dark:bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500"
              @keydown.enter.prevent="applyBulkUpdate"
            />
          </div>
          <div class="flex gap-2">
            <button
              class="flex-1 px-3 py-1.5 text-xs font-medium bg-woot-500 text-white rounded hover:bg-woot-600 transition-colors disabled:opacity-50"
              :disabled="isBulkLoading"
              @click="applyBulkUpdate"
            >
              {{ isBulkLoading ? 'Applying…' : 'Apply to selected' }}
            </button>
            <button
              class="px-3 py-1.5 text-xs font-medium border border-n-weak rounded hover:bg-n-alpha-1 text-n-slate-11 transition-colors"
              @click="closeBulkPanel"
            >
              Cancel
            </button>
          </div>
          </div>
        </div>
        <button
          class="ml-auto text-xs text-n-slate-9 hover:text-n-slate-12 transition-colors"
          @click="clearSelection"
        >
          Clear selection
        </button>
      </div>

      <div class="overflow-x-auto">
      <table class="w-full text-sm border-collapse">
        <thead>
          <tr class="border-b border-n-weak">
            <th class="px-3 py-2.5 w-10 bg-n-surface-1">
              <input
                type="checkbox"
                class="w-3.5 h-3.5 accent-woot-500 cursor-pointer"
                :checked="companies.length > 0 && selectedCompanyIds.length === companies.length"
                :indeterminate="selectedCompanyIds.length > 0 && selectedCompanyIds.length < companies.length"
                @change="e => toggleSelectAll(e.target.checked)"
              />
            </th>
            <th
              v-for="fd in fieldDefs"
              :key="fd.id"
              class="relative text-left px-4 py-2.5 text-xs font-medium text-n-slate-9 whitespace-nowrap min-w-[140px] bg-n-surface-1 select-none"
              :style="colStyle(fd.field_key)"
            >
              {{ fd.label }}
              <div class="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-woot-500/40 z-10" @mousedown.stop="startResize($event, fd.field_key)" />
            </th>
            <th class="relative text-left px-4 py-2.5 text-xs font-medium text-n-slate-9 whitespace-nowrap bg-n-surface-1 select-none" :style="colStyle('__contacts')">
              Contacts
              <div class="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-woot-500/40 z-10" @mousedown.stop="startResize($event, '__contacts')" />
            </th>
            <th class="relative text-left px-4 py-2.5 text-xs font-medium text-n-slate-9 whitespace-nowrap bg-n-surface-1 select-none" :style="colStyle('__updated')">
              Updated
              <div class="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-woot-500/40 z-10" @mousedown.stop="startResize($event, '__updated')" />
            </th>
            <th class="px-4 py-2.5 bg-n-surface-1"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="company in companies"
            :key="company.id"
            class="border-b border-n-weak hover:bg-n-alpha-1 cursor-pointer transition-colors"
            :class="{ 'bg-n-alpha-2': isCompanySelected(company.id) }"
            @click="goToCompany(company.id)"
          >
            <td class="px-3 py-3 w-10" @click.stop>
              <input
                type="checkbox"
                :checked="isCompanySelected(company.id)"
                class="w-3.5 h-3.5 accent-woot-500 cursor-pointer"
                @change="e => toggleCompanySelect(company.id, e.target.checked)"
              />
            </td>
            <td
              v-for="fd in fieldDefs"
              :key="fd.id"
              class="px-4 py-3 text-sm text-n-slate-11 max-w-[180px] transition-colors"
              :class="fd.read_only ? '' : 'hover:bg-n-alpha-2 cursor-text'"
              @click.stop="startInlineEdit($event, company, fd)"
            >
              <template v-if="editingCell && editingCell.companyId === company.id && editingCell.fieldKey === fd.field_key">
                <select
                  v-if="fd.field_type === 'list'"
                  v-model="editingValue"
                  :ref="el => { if (el) { nextTick(() => el.focus()); } }"
                  class="w-full px-1.5 py-0.5 text-sm rounded border border-woot-400 bg-white dark:bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500"
                  @change="saveInlineEdit"
                  @keydown.esc.prevent="cancelInlineEdit"
                  @blur="saveInlineEdit"
                  @click.stop
                >
                  <option value="">— select —</option>
                  <option v-for="opt in fd.options" :key="opt" :value="opt">{{ opt }}</option>
                </select>
                <input
                  v-else
                  v-model="editingValue"
                  :ref="el => { if (el) { nextTick(() => el.focus()); } }"
                  :type="fd.field_type === 'number' ? 'number' : fd.field_type === 'date' ? 'date' : 'text'"
                  class="w-full px-1.5 py-0.5 text-sm rounded border border-woot-400 bg-white dark:bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500"
                  @keydown.enter.prevent="saveInlineEdit"
                  @keydown.esc.prevent="cancelInlineEdit"
                  @blur="saveInlineEdit"
                  @click.stop
                />
              </template>
              <span v-else class="block truncate">{{ displayFieldValue(company, fd) }}</span>
            </td>
            <td class="px-4 py-3 text-sm text-n-slate-11">{{ company.contactsCount || 0 }}</td>
            <td class="px-4 py-3 text-sm text-n-slate-9 whitespace-nowrap">{{ formatDate(company.updatedAt) }}</td>
            <td class="px-4 py-3" @click.stop>
              <div class="flex items-center gap-1">
                <button
                  class="p-1.5 text-n-slate-9 hover:text-woot-500 rounded transition-colors"
                  title="View details"
                  @click="goToCompany(company.id)"
                >
                  <span class="i-lucide-arrow-right size-3.5" />
                </button>
                <button
                  class="p-1.5 text-n-slate-9 hover:text-woot-500 rounded transition-colors"
                  title="Edit"
                  @click="openEdit(company.id)"
                >
                  <span class="i-lucide-pencil size-3.5" />
                </button>
                <button
                  class="p-1.5 text-n-slate-9 hover:text-red-500 rounded transition-colors"
                  title="Delete"
                  @click="deleteCompany(company.id)"
                >
                  <span class="i-lucide-trash-2 size-3.5" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      </div>
    </div>
  </CompaniesListLayout>

  <!-- Import dialog -->
  <CompanyImportDialog ref="importDialogRef" @import="onImportFile" />

  <!-- Add / Edit dialog -->
  <Dialog
    ref="dialogRef"
    :title="editingId ? 'Edit Company' : 'Add Company'"
    :confirm-button-label="editingId ? 'Update' : 'Create'"
    cancel-button-label="Cancel"
    :disable-confirm-button="!(form.extraFields?.company_domain || '').trim()"
    @confirm="saveCompany"
    @close="closeDialog"
  >
    <div class="flex flex-col gap-4">
      <!-- Extra (custom) fields -->
      <div v-for="fd in fieldDefs" :key="fd.id">
        <label class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-1.5">
          {{ fd.label }}
          <span v-if="fd.field_type === 'auto_number'" class="ml-1 text-n-slate-8 normal-case font-normal">(auto-generated)</span>
          <span v-if="fd.required" class="ml-1 text-red-500">*</span>
        </label>

        <!-- Auto-number: read-only -->
        <template v-if="fd.field_type === 'auto_number'">
          <div class="px-3 py-2 text-sm rounded-lg border border-n-weak/60 bg-n-alpha-1 text-n-slate-9 font-mono">
            {{ form.extraFields[fd.field_key] || '— assigned on save —' }}
          </div>
        </template>

        <!-- Checkbox -->
        <template v-else-if="fd.field_type === 'checkbox'">
          <input
            v-model="form.extraFields[fd.field_key]"
            type="checkbox"
            class="w-4 h-4 rounded accent-woot-500"
          />
        </template>

        <!-- List (select) -->
        <template v-else-if="fd.field_type === 'list'">
          <select
            v-model="form.extraFields[fd.field_key]"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak dark:bg-n-alpha-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500"
          >
            <option value="">— select —</option>
            <option v-for="opt in fd.options" :key="opt" :value="opt">{{ opt }}</option>
          </select>
        </template>

        <!-- Date -->
        <template v-else-if="fd.field_type === 'date'">
          <input
            v-model="form.extraFields[fd.field_key]"
            type="date"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak dark:bg-n-alpha-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500"
          />
        </template>

        <!-- Number -->
        <template v-else-if="fd.field_type === 'number'">
          <input
            v-model="form.extraFields[fd.field_key]"
            type="number"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak dark:bg-n-alpha-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500"
          />
        </template>

        <!-- Text / Link fallback -->
        <template v-else>
          <input
            v-model="form.extraFields[fd.field_key]"
            :type="fd.field_type === 'link' ? 'url' : 'text'"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak dark:bg-n-alpha-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500"
          />
        </template>
      </div>

    </div>
  </Dialog>

  <!-- Filter Panel -->
  <Teleport to="body">
    <div
      v-if="showFilterPanel"
      class="fixed bg-n-solid-2 border border-n-weak rounded-xl shadow-xl flex flex-col gap-3 p-4"
      :style="filterPanelStyle"
    >
      <div class="flex items-center justify-between">
        <span class="text-sm font-semibold text-n-slate-12">Filter Companies</span>
        <button class="text-xs text-n-slate-9 hover:text-n-slate-12" @click="clearFilters">Clear all</button>
      </div>

      <div v-for="(filter, idx) in activeFilters" :key="idx" class="flex flex-col gap-1.5">
        <div class="flex items-center gap-2">
          <select
            :value="filter.field"
            class="flex-1 px-2 py-1.5 text-xs rounded-lg border border-n-weak bg-white dark:bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500"
            @change="updateFilter(idx, 'field', $event.target.value)"
          >
            <option v-for="f in filterFields" :key="f.value" :value="f.value">{{ f.label }}</option>
          </select>

          <select
            :value="filter.op"
            class="w-36 px-2 py-1.5 text-xs rounded-lg border border-n-weak bg-white dark:bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500"
            @change="updateFilter(idx, 'op', $event.target.value)"
          >
            <option v-for="op in FILTER_OPERATORS" :key="op.value" :value="op.value">{{ op.label }}</option>
          </select>

          <button class="text-n-slate-9 hover:text-red-500 flex-shrink-0" @click="removeFilterRow(idx)">
            <span class="i-lucide-x size-3.5" />
          </button>
        </div>

        <input
          v-if="!['is_present','is_not_present'].includes(filter.op)"
          :value="filter.value"
          type="text"
          placeholder="Enter value…"
          class="w-full px-2 py-1.5 text-xs rounded-lg border border-n-weak bg-white dark:bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500"
          @input="updateFilter(idx, 'value', $event.target.value)"
          @keydown.enter="applyFilters"
        />
      </div>

      <button
        class="text-xs text-woot-500 hover:text-woot-600 flex items-center gap-1 w-fit"
        @click="addFilterRow"
      >
        <span class="i-lucide-plus size-3.5" /> Add condition
      </button>

      <div class="flex items-center gap-2 pt-1 border-t border-n-weak">
        <button
          class="flex-1 px-3 py-1.5 text-xs font-medium bg-woot-500 text-white rounded-lg hover:bg-woot-600 transition-colors"
          @click="applyFilters"
        >
          Apply
        </button>
        <button
          class="px-3 py-1.5 text-xs font-medium border border-n-weak rounded-lg hover:bg-n-alpha-1 text-n-slate-11 transition-colors"
          @click="() => { showFilterPanel = false }"
        >
          Cancel
        </button>
      </div>
    </div>
  </Teleport>
</template>
